import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["startTime", "endTime", "form"]

  connect() {
    this.markInTime = null
    this.marking = false
    this.videoReady = false
    this.waitForVideo()
    this.boundKeydown = this.handleKeydown.bind(this)
    this.boundKeyup = this.handleKeyup.bind(this)
    document.addEventListener("keydown", this.boundKeydown, true)
    document.addEventListener("keyup", this.boundKeyup, true)
    this.seekHeld = false
    this.savedRate = null
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown, true)
    document.removeEventListener("keyup", this.boundKeyup, true)
  }

  waitForVideo() {
    const video = this.video
    if (!video) return

    const enable = () => { this.videoReady = true }

    if (video.readyState >= 1) {
      enable()
    } else {
      video.addEventListener("loadedmetadata", enable, { once: true })
    }
  }

  get video() {
    return this.element.querySelector("video")
  }

  get plyrContainer() {
    return this.element.querySelector(".plyr")
  }

  handleKeydown(event) {
    if (event.target.tagName === "INPUT" || event.target.tagName === "TEXTAREA") return
    if (!this.videoReady) return

    if (event.key === "m" || event.key === "M") {
      event.preventDefault()
      if (this.marking) {
        this.markOut()
      } else {
        this.markIn()
      }
    } else if ((event.key === "c" || event.key === "C") && this.marking) {
      event.preventDefault()
      this.cancel()
    } else if (event.key === "f" || event.key === "F") {
      event.preventDefault()
      this.toggleFullscreen()
    } else if (event.key === "p" || event.key === "P") {
      event.preventDefault()
      event.stopImmediatePropagation()
      const video = this.video
      if (video) video.paused ? video.play() : video.pause()
    } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault()
      event.stopImmediatePropagation()
      this.handleSeek(event)
      return
    }
  }

  handleKeyup(event) {
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      this.stopFastSeek()
    }
  }

  handleSeek(event) {
    const video = this.video
    if (!video) return

    if (this.seekHeld) return // Already holding, ignore repeat keydowns

    this.seekHeld = true
    this.seekDirection = event.key === "ArrowLeft" ? -1 : 1
    this.seekSkipped = false

    // Wait briefly — if released before this fires, do a 5s skip
    // If still held, start fast seeking
    this.savedRate = video.playbackRate
    this.wasPlaying = !video.paused

    this.seekTimeout = setTimeout(() => {
      this.seekSkipped = true

      if (this.seekDirection === 1) {
        if (!this.wasPlaying) video.play()
        video.playbackRate = 4
      } else {
        video.pause()
        // Hide Plyr's play button during reverse seek
        const playBtn = this.plyrContainer?.querySelector(".plyr__control--overlaid")
        if (playBtn) playBtn.style.display = "none"
        this.reverseInterval = setInterval(() => {
          video.currentTime = Math.max(0, video.currentTime - 0.15)
        }, 30)
      }
    }, 250)
  }

  stopFastSeek() {
    if (!this.seekHeld) return
    this.seekHeld = false

    clearTimeout(this.seekTimeout)

    const video = this.video
    if (!video) return

    if (!this.seekSkipped) {
      // Short press — jump 5 seconds
      video.currentTime = Math.max(0, video.currentTime + (5 * this.seekDirection))
      if (this.wasPlaying) video.play()
    } else {
      // Was fast seeking — restore state
      clearInterval(this.reverseInterval)
      this.reverseInterval = null
      video.playbackRate = this.savedRate || 1
      this.savedRate = null
      if (this.wasPlaying) {
        video.play().then(() => {
          const playBtn = this.plyrContainer?.querySelector(".plyr__control--overlaid")
          if (playBtn) playBtn.style.display = ""
        })
      } else {
        video.pause()
        const playBtn = this.plyrContainer?.querySelector(".plyr__control--overlaid")
        if (playBtn) playBtn.style.display = ""
      }
    }
  }

  markIn() {
    const video = this.video
    if (!video) return

    this.markInTime = video.currentTime
    this.startTimeTarget.value = this.markInTime
    this.marking = true
    this.showMarkingBorder()
  }

  markOut() {
    const video = this.video
    if (!video || this.markInTime === null) return

    const endTime = video.currentTime
    if (endTime <= this.markInTime) return

    this.endTimeTarget.value = endTime
    this.formTarget.requestSubmit()

    this.markInTime = null
    this.marking = false
    this.hideMarkingBorder()
  }

  cancel() {
    this.markInTime = null
    this.marking = false
    this.startTimeTarget.value = ""
    this.flashBorder("#dc2626")
  }

  toggleFullscreen() {
    const container = this.plyrContainer
    if (!container) return

    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      container.requestFullscreen()
    }
  }

  showMarkingBorder() {
    const container = this.plyrContainer
    if (!container) return

    let overlay = container.querySelector("#marking-border")
    if (!overlay) {
      overlay = document.createElement("div")
      overlay.id = "marking-border"
      overlay.style.cssText = "position:absolute;inset:0;border:3px solid #16a34a;pointer-events:none;z-index:100;transition:border-color 0.3s;"
      container.style.position = "relative"
      container.appendChild(overlay)
    }
    overlay.style.display = "block"
  }

  flashBorder(color) {
    const container = this.plyrContainer
    if (!container) return

    let overlay = container.querySelector("#marking-border")
    if (!overlay) {
      overlay = document.createElement("div")
      overlay.id = "marking-border"
      overlay.style.cssText = "position:absolute;inset:0;pointer-events:none;z-index:100;transition:opacity 0.4s;"
      container.style.position = "relative"
      container.appendChild(overlay)
    }
    overlay.style.borderColor = color
    overlay.style.border = `3px solid ${color}`
    overlay.style.display = "block"
    overlay.style.opacity = "1"

    setTimeout(() => {
      overlay.style.opacity = "0"
      setTimeout(() => {
        overlay.style.display = "none"
        overlay.style.opacity = "1"
      }, 400)
    }, 200)
  }

  hideMarkingBorder() {
    const container = this.plyrContainer
    if (!container) return

    const overlay = container.querySelector("#marking-border")
    if (overlay) {
      overlay.style.display = "none"
    }
  }
}
